// Feedback — one row of a feedback board: a vote toggle with its count, then
// the post's heading and one meta line. The component ships NO content of its
// own. Every word and number comes from the row's props:
//
//   heading — the post's heading. No heading → nothing renders.
//   meta    — one line under the heading (author, age, comment count),
//             composed and translated by the consumer. Empty → no line.
//   upvotes — the vote count, a number. Not a number → no vote toggle.
//   voted   — true when the person's own vote is already on.
//
//   Feedback: { heading: (el, s) => s.post.title, upvotes: (el, s) => s.post.votes }
//   Board: { childExtends: 'Feedback', children: (el, s) => s.posts }  // each item → props
//
// Props, not state: a prop never falls back to the root state's key of the
// same name, and the row adds no state scope of its own, so an element that
// only SHARES the key `Feedback` (a generated app's message, a toast) keeps
// its own state and shows only its own content. The vote toggle keeps its
// one flag on its own state; a consumer that saves votes sets
// `Button: { onClick }`. Its accessible name is the count until a consumer
// sets `Button: { aria: { label } }` — the catalogs hold no "upvote" term.
//
// Why: the component carried a whole demo post ("Flexbox in Editor", "by
// kiaynwang", "3 hours ago", "49 comments"). A child key named `Feedback`
// extends this component, so a generated app showed a stranger's post on a
// person's screen (R2-M1, persona round 2).
// __tests__/feedbackShipsNoContent.test.js guards it.
export const Feedback = {
  flow: 'x',
  gap: 'A2',
  align: 'flex-start',

  Button: {
    color: 'paragraph',
    flow: 'column',
    gap: 'Z',
    padding: '0',
    background: 'transparent',
    border: '0',
    fontSize: 'A',
    cursor: 'pointer',
    margin: '-W - -',
    state: { flipped: false },
    hide: (el) => !el.parent.heading || typeof el.parent.upvotes !== 'number',
    text: (el, s) => {
      const row = el.parent
      if (typeof row.upvotes !== 'number') return ''
      const step = row.voted ? -1 : 1
      return String(row.upvotes + (s.flipped ? step : 0))
    },
    aria: {
      pressed: (el, s) => (Boolean(el.parent.voted) !== Boolean(s.flipped) ? 'true' : 'false')
    },
    Icon: {
      name: 'arrowAngleUp',
      // eslint-disable-next-line no-restricted-syntax -- icon glyph color, not text
      color: 'inactive'
    },
    // The glyph follows aria-pressed, so what a person sees and what a screen
    // reader hears cannot disagree. Hover takes the next ink up: `placeholder`
    // (the old hover) is the same pair as `inactive`, so it changed nothing.
    '[aria-pressed="true"] svg': {
      color: 'orange'
    },
    '[aria-pressed="false"]:hover svg': {
      color: 'paragraph'
    },
    onClick: (ev, el, s) => {
      s.update({ flipped: !s.flipped })
    }
  },

  Hgroup: {
    hide: (el) => !el.parent.heading,
    H: {
      tag: 'h6',
      fontWeight: '700',
      text: (el) => el.parent.parent.heading
    },
    P: {
      hide: (el) => !el.parent.parent.meta,
      text: (el) => el.parent.parent.meta
    }
  }
}
